import { forwardRef } from 'react';

import MuiAlert from '@mui/material/Alert';
import MuiAlertTitle from '@mui/material/AlertTitle';
import MuiStack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';

import { alertVariantToMuiSeverity } from './helpers';
import { AlertProps } from './types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

const Alert = forwardRef<HTMLDivElement, React.PropsWithChildren<AlertProps>>(
  (
    {
      title,
      children,
      footer,
      variant = 'default',
      isFullWidth = false,
      onClose,
      actionComponent,
    },
    ref,
  ) => {
    const theme = useTheme();

    const severity = alertVariantToMuiSeverity[variant];

    return (
      <Stack
        sx={[
          {
            border: '1px',
            borderColor: theme.palette.colors.grey[200],
            borderStyle: 'solid',
            borderRadius: '5px',
            width: 'fit-content',
          },
        ]}
      >
        <MuiAlert
          icon={false}
          ref={ref}
          severity={severity}
          action={actionComponent}
          sx={[
            {
              typography: 'font12',
              backgroundColor: theme.palette.colors.white,
              boxShadow: 3,
              borderLeftWidth: '10px',
              borderLeftColor: `${severity}.main`,
              borderLeftStyle: 'solid',
              color: theme.palette.colors.grey[600],
              lineHeight: '20px',
              padding: '8px 16px',
              '& .MuiAlert-action': {
                paddingTop: 0,
                color: theme.palette.colors.grey[600],
                alignItems: footer ? 'flex-start' : 'center',
              },
            },
            isFullWidth && {
              '& .MuiAlert-message': {
                width: '100%',
              },
            },
            !isFullWidth && {
              width: 'fit-content',
            },
          ]}
          onClose={onClose}
        >
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
          >
            <MuiAlertTitle
              sx={{
                color: theme.palette.colors.black,
                typography: 'font14Bold',
                marginBottom: footer ? 0.5 : 0,
              }}
            >
              {title}
            </MuiAlertTitle>
          </Box>
          {(children || footer) && (
            <MuiStack>
              {children}
              {footer && <AlertFooter>{footer}</AlertFooter>}
            </MuiStack>
          )}
        </MuiAlert>
      </Stack>
    );
  },
);

const AlertFooter = styled('div')`
  display: flex;
  justify-content: end;
  padding-top: 8px;
`;

Alert.displayName = 'Alert';

export default Alert;
